import moment from 'moment';
import { DAY, MONTH } from '../../constants';
import { solidBlueColor } from '../../constants/colors';
import formatNumber from './FormatNumber';
import formatEventName from './FormatEventName';

// Keep the first series visually distinct in the legend and on the chart.
// The fallback prevents exact repeats when there are more events than colors.
const datasetColors = [
  '#1F77B4', '#FF7F0E', '#2CA02C', '#D62728', '#9467BD',
  '#8C564B', '#E377C2', '#7F7F7F', '#BCBD22', '#17BECF',
  '#393B79', '#637939', '#8C6D31', '#843C39', '#7B4173',
  '#3182BD', '#31A354', '#756BB1', '#E6550D', '#636363',
];

const getDatasetColor = (index) => datasetColors[index]
  ?? `hsl(${(index * 137.508) % 360} 68% 52%)`;

export default function ViewsAndSalesChartService(fullData, data, activeButton, type, t, currency='') {
  const formattedData = {
    labels: [],
    datasets: []
  };

  const allDates = Object.keys(data).flatMap(event => Object.keys(data[event]));
  const validDates = allDates.filter(date => date !== 'null');

  const minDate = validDates.length > 0 ? new Date(Math.min(...validDates.map(date => new Date(date)))) : null;
  const maxDate = validDates.length > 0 ? new Date(Math.max(...validDates.map(date => new Date(date)))) : null;

  const selectedDates = [];

  if (minDate && maxDate) {
    const currentDate = new Date(minDate);

    while (currentDate <= maxDate) {
      selectedDates.push(new Date(currentDate).toISOString());
      currentDate.setDate(currentDate.getDate() + 1);
    }
  } else {
    selectedDates.push(...validDates);
  }

  const getAggregationKey = date => {
    if (activeButton === DAY) return date;
    if (activeButton === MONTH) return moment(date).format('YYYY-MM');
    return moment(date).format('YYYY');
  };
  const selectedKeys = [...new Set(selectedDates.map(getAggregationKey))];

  formattedData.labels = selectedKeys.map(key => {
    if (activeButton === DAY) return moment(key).format('ddd D, YYYY');
    if (activeButton === MONTH) return moment(`${key}-01`).format('MMMM YYYY');
    return key;
  });

  Object.keys(data).forEach((event, index) => {
    const eventData = data[event];

    const valuesByKey = Object.entries(eventData).reduce((result, [date, values]) => {
      if (date === 'null') return result;
      const key = getAggregationKey(date);
      result[key] = (result[key] ?? 0) + (values[type] ?? 0);
      return result;
    }, {});
    const eventTypeData = selectedKeys.map(key =>
      Object.prototype.hasOwnProperty.call(valuesByKey, key)
        ? valuesByKey[key]
        : activeButton === DAY
          ? null
          : 0
    );

    const total = currency + formatNumber(Object.values(eventData).reduce((acc,el) => acc+el[type] ,0))
    const color = Object.keys(data).length === 1 && type === 'views'
      ? solidBlueColor
      : getDatasetColor(index);
    const ticketName = fullData.tickets?.[event] || event;
    const eventLabel = fullData.singleEventsTicketsStatistic
      ? Object.keys(data).length === 1 && type === 'views'
        ? t('ticketStatistic.totalViews')
        : ticketName
      : formatEventName(
        fullData.events[event],
        event,
        fullData.eventMeta,
        t('ticketStatistic.archive')
      );

    formattedData.datasets.push({
      label: `${eventLabel} (${total})`,
      tooltipLabel: eventLabel,
      tooltipCurrency: currency.trim(),
      data: eventTypeData,
      backgroundColor: color,
      fill: false,
      lineTension: 0.1,
      borderColor: color,
      borderWidth: 2,
      borderDash: [],
      borderDashOffset: 0.0,
      pointBackgroundColor: color,
      pointBorderWidth: 0,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: color,
      pointHoverBorderWidth: 10,
    });
  });
  return formattedData
}
